import { utc } from '@date-fns/utc';
import { useQueryClient } from '@tanstack/vue-query';
import { startOfDay } from 'date-fns';
import { noop, omit } from 'lodash-es';
import { computed, ref } from 'vue';

import {
  type Booking,
  type BookingEquip,
  type BookingId,
  type BookingRoomHours,
  calendarAdd,
  checkRoomEquipUnavailable,
  checkRoomHoursUnavailable,
  type Equip,
  type EquipGroup,
  type EquipGroupId,
  type EquipId,
  type RoomPromo,
  type Room,
  type RoomHours,
  type RoomId,
  type UserCalendar,
} from '@scaffold/common/booking';
import settings from '@scaffold/common/settings';
import {
  type ArrayAndMap,
  arrayAndMap,
  getNaiveToday as getNaiveTodayEx,
} from '@scaffold/common/utils';

import { getDayCalendar, getDayEquipCalendar } from '@/api/booking';

export const sortCalendar =
  (rooms: ArrayAndMap<Room>) =>
  (a: RoomHours & { tag?: string }, b: RoomHours & { tag?: string }) => {
    const diff =
      (rooms[a.room]?.index ?? Number.MAX_SAFE_INTEGER - 1000) +
      (a.tag === 'unavailable' ? 999 : 0) -
      (rooms[b.room]?.index ?? Number.MAX_SAFE_INTEGER - 1000) -
      (b.tag === 'unavailable' ? 999 : 0);

    if (diff < 0) return -1;
    if (diff > 0 || a.start > b.start) return 1;
    return 0;
  };

export const sortEquip = (a: BookingEquip, b: BookingEquip) => {
  if (!a.tag && b.tag) return -1;
  if (a.tag && !b.tag) return 1;

  return a.name.localeCompare(b.name);
};

export function makeEquip(
  equipment: Equip[],
  groups: ArrayAndMap<EquipGroup>,
  allGroups: boolean = false,
) {
  const equipGroups: { [key: EquipGroupId]: Equip[] } = allGroups
    ? Object.keys(groups).reduce(
        (groups, group) => {
          groups[group] = [];
          return groups;
        },
        {} as { [key: string]: Equip[] },
      )
    : {};

  for (const eq of equipment) {
    if (equipGroups[eq.group]) equipGroups[eq.group].push(eq);
    else equipGroups[eq.group] = [eq];
  }

  return {
    equip: arrayAndMap(equipment),
    equipGroups: (Object.entries(equipGroups) as [EquipGroupId, Equip[]][])
      .map(([group, list]) =>
        Object.assign(
          list.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, {
              sensitivity: 'base',
            }),
          ),
          {
            ...(groups[group] ?? {
              index: -1,
              id: group,
              name: group,
              title: group,
            }),
          } as EquipGroup,
        ),
      )
      .sort((a, b) => Math.sign(a.index - b.index)),
  };
}

export function paymentBadgeClss(via: keyof Booking['sum']) {
  switch (via) {
    case 'unpaid':
      return 'bg-danger';
    case 'sumup':
      return 'bg-info';
    case 'cash':
      return 'bg-dark';
    default:
      return 'bg-secondary';
  }
}

export const getNaiveToday = () => getNaiveTodayEx(settings.time_zone);

export function getStartCalendarDay(calendar?: { start: Date }[]) {
  const today = getNaiveToday();

  let day: Date | undefined;
  if (calendar) {
    for (const { start } of calendar) if (!day || day > start) day = start;
  }

  if (!day || day < today) return today;

  return startOfDay(day, { in: utc }) as Date;
}

export function isBookingUnavailable({
  booking,
  calendar,
}: {
  booking: Booking;
  calendar?: (UserCalendar & { tag?: string })[];
}) {
  if (booking.type === 'room') {
    return (
      !!calendar?.some((c) => c.tag === 'unavailable') ||
      !!booking.equipment?.some((eq) => eq.tag === 'unavailable')
    );
  }

  return false;
}

export function useCalendarAvailability(
  env: { rooms: ArrayAndMap<Room> },
  calendar: BookingRoomHours[],
  bid?: BookingId,
) {
  const queryClient = useQueryClient();

  const loadingCount = ref(0);

  let updateCalendarAvailabilityController: AbortController | undefined;
  async function updateCalendarAvailability(promo: RoomPromo | undefined) {
    if (import.meta.env.SSR) return;

    loadingCount.value++;
    try {
      updateCalendarAvailabilityController?.abort();
      const { signal } = (updateCalendarAvailabilityController =
        new AbortController());

      const localCalendar = [...calendar];

      await checkRoomHoursUnavailable(env, localCalendar, (day: Date) => {
        if (signal.aborted) throw new Error('Aborted');

        return queryClient
          .fetchQuery(getDayCalendar.query(day))
          .then((calendar) =>
            bid ? calendar.filter((c) => c.bid !== bid) : calendar,
          );
      }).catch(noop);

      if (signal.aborted) return;

      const hasAffordable = promo?.pricing?.some((p) => p.tag === 'affordable');

      // Merge affordable calendar and available calendar
      if (!hasAffordable) {
        const newCalendar: BookingRoomHours[] = [];
        for (const c of localCalendar) {
          calendarAdd(newCalendar, c.tag === 'affordable' ? omit(c, 'tag') : c);
        }

        newCalendar.sort(sortCalendar(env.rooms));
        calendar.length = 0;
        calendar.push(...newCalendar);
      } else {
        localCalendar.sort(sortCalendar(env.rooms));
        calendar.length = 0;
        calendar.push(...localCalendar);
      }
    } finally {
      loadingCount.value--;
    }
  }

  return {
    updateCalendarAvailability,
    isCalendarAvailabilityLoading: computed(() => loadingCount.value > 0),
  };
}

export function useEquipAvailability(
  calendar: BookingRoomHours[],
  bid?: BookingId,
) {
  const queryClient = useQueryClient();

  const loadingCount = ref(0);

  const unavailableEquip = ref<Set<EquipId>>();

  let updateEquipAvailabilityController: AbortController | undefined;
  async function updateEquipAvailability() {
    if (import.meta.env.SSR) return;

    loadingCount.value++;
    try {
      updateEquipAvailabilityController?.abort();
      const { signal } = (updateEquipAvailabilityController =
        new AbortController());

      const unavailable = await checkRoomEquipUnavailable(
        calendar,
        null,
        (day: Date) => {
          if (signal.aborted) throw new Error('Aborted');

          return queryClient
            .fetchQuery(getDayEquipCalendar.query(day))
            .then((calendar) =>
              bid ? calendar.filter((c) => c.bid !== bid) : calendar,
            );
        },
      ).catch(noop);

      if (!unavailable || signal.aborted) return;

      unavailableEquip.value = unavailable;
    } finally {
      loadingCount.value--;
    }
  }

  return {
    updateEquipAvailability,
    unavailableEquip,
    isEquipAvailabilityLoading: computed(() => loadingCount.value > 0),
  };
}

export function getRoomTimes(rooms: Room[]) {
  const roomTimes: Record<RoomId, string[]> = {};
  for (const room of rooms) {
    const times: string[] = [];
    for (let h = room.start; h <= room.end; h++) {
      times.push(
        `${Math.floor(h).toString().padStart(2, '0')}:${Math.floor((h % 1) * 60)
          .toString()
          .padStart(2, '0')}`,
      );
    }
    roomTimes[room.id] = times;
  }
  return roomTimes;
}
